import { useState } from 'react';
import { Database, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TicketDetail } from '@uniticket/shared';

interface Props {
  ticket: TicketDetail;
}

export function TicketDMPPanel({ ticket }: Props) {
  const { t } = useTranslation('tickets');
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Database className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-800">{t('detail.orderData')}</p>
            <p className="text-xs text-slate-400">{ticket.dmpOrderId ?? t('detail.noOrderId')}</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-6 pb-5 border-t border-slate-100">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 pt-4">
            <DMPField label={t('common:fields.orderId')} value={ticket.dmpOrderId} mono />
            <DMPField label={t('common:fields.orderType')} value={ticket.orderType} />
            <DMPField label={t('common:fields.jvid')} value={ticket.jvid != null ? String(ticket.jvid) : null} mono />
            {ticket.utac && (
              <DMPField label={t('common:fields.utac')} value={ticket.utac} mono />
            )}
            {ticket.customerReference && (
              <DMPField label={t('common:fields.customerReference')} value={ticket.customerReference} mono />
            )}
            {ticket.moveReason && (
              <DMPField label={t('common:fields.moveReason')} value={ticket.moveReason} />
            )}
            {ticket.utacSwap1 && (
              <DMPField label={t('common:fields.utacSwap1')} value={ticket.utacSwap1} mono />
            )}
            {ticket.utacSwap2 && (
              <DMPField label={t('common:fields.utacSwap2')} value={ticket.utacSwap2} mono />
            )}
            {ticket.splitterSwapFrom && (
              <DMPField label={t('common:fields.splitterSwapFrom')} value={ticket.splitterSwapFrom} mono />
            )}
            {ticket.splitterSwapTo && (
              <DMPField label={t('common:fields.splitterSwapTo')} value={ticket.splitterSwapTo} mono />
            )}
            {ticket.operatorOrderRef && (
              <DMPField label={t('common:fields.operatorOrderRef')} value={ticket.operatorOrderRef} />
            )}
            {ticket.customerNumber && (
              <DMPField label={t('common:fields.customerNumber')} value={ticket.customerNumber} />
            )}
            {(ticket.orderSubcategory === 'DISTANCE_INQUIRY' || ticket.metersPublic != null || ticket.metersPrivate != null || ticket.metersIndoor != null) && (
              <>
                <DMPField label={t('common:fields.metersPublic')} value={ticket.metersPublic != null ? `${ticket.metersPublic} m` : null} />
                <DMPField label={t('common:fields.metersPrivate')} value={ticket.metersPrivate != null ? `${ticket.metersPrivate} m` : null} />
                <DMPField label={t('common:fields.metersIndoor')} value={ticket.metersIndoor != null ? `${ticket.metersIndoor} m` : null} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DMPField({ label, value, mono, badge }: { label: string; value?: string | null; mono?: boolean; badge?: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      {badge ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
          {badge}
        </span>
      ) : (
        <p className={`text-sm ${mono ? 'font-mono font-medium text-slate-800' : 'text-slate-700'}`}>{value ?? '—'}</p>
      )}
    </div>
  );
}
